import { ASSET_SPECS, AssetSpecId } from 'fym-shared/src/music24/assetSpecs';
import type { AssetShareKind } from 'fym-shared/src/music24/assetShares';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { SerializedReel } from '../types';
import { fileHasWatermark } from '../../watermarkUpsell/files/watermarkedFile';

/**
 * One file that the asset viewer shows (design music24-asset-viewer a1, a2): a ready kit asset
 * version or a ready AI reel. Each surface builds the ordered list of the song; the arrows move
 * through it. Pure data, so the list builders are tested without React.
 */
export interface AssetViewerItem {
  /** `kitAsset:<uuid>` or `reel:<uuid>`: the key a card opens the viewer on. */
  key: string;
  /** The kind of the share link (`/accounts/:id/asset-shares`). */
  kind: AssetShareKind;
  assetUuid: string;
  /** The asset type; `aiReel` for a reel. */
  themeId: StudioThemeId;
  /** The variant of a kit asset ("outNow", "3000"); null for a reel. */
  variant: string | null;
  specId: AssetSpecId | null;
  mediaKind: 'image' | 'video';
  /** The relative `files/<uuid>/download` url of the file (Download). */
  fileUrl: string;
  /** What the page shows or plays: the H.264 preview of the motion artwork, else the file. */
  playUrl: string;
  posterUrl: string | null;
  /** A loop asset (the Canvas loop, the motion artwork) plays with `loop`. */
  loop: boolean;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  sizeBytes: number | null;
  /** The file type of the download: JPG, PNG, MP4, MOV. */
  fileType: string;
  /** The version number of a kit asset; null for a reel. */
  version: number | null;
  /** True when no newer version replaced this one. */
  isLatest: boolean;
  madeAt: string;
  releaseThemeId: ReleaseThemeId;
  /** The file carries the Music24 mark: a file of Free or the trial (design a5; trial-upsell a3). */
  watermark: boolean;
  /** A preview render of the Free plan or the trial: low resolution (pricing spec 2026-10-08). */
  preview: boolean;
  /** The source row: "Post it" and "Make again" read it. */
  asset: SerializedKitAsset | null;
  reel: SerializedReel | null;
}

export function kitAssetViewerKey(uuid: string): string {
  return `kitAsset:${uuid}`;
}

export function reelViewerKey(uuid: string): string {
  return `reel:${uuid}`;
}

/** The viewer item of a ready kit asset with a file, else null. */
export function kitAssetViewerItem(asset: SerializedKitAsset): AssetViewerItem | null {
  if (asset.status !== 'ready' || asset.fileUrl === null) {
    return null;
  }
  const spec = ASSET_SPECS[asset.specId];
  const isVideo = spec?.kind === 'video' || asset.durationSec !== null;
  return {
    key: kitAssetViewerKey(asset.uuid),
    kind: 'kitAsset',
    assetUuid: asset.uuid,
    themeId: asset.themeId,
    variant: asset.variant,
    specId: asset.specId,
    mediaKind: isVideo ? 'video' : 'image',
    fileUrl: asset.fileUrl,
    // An old motion artwork row plays its H.264 preview; a new one is an MP4 that plays itself.
    playUrl: isVideo ? (asset.previewUrl ?? asset.fileUrl) : asset.fileUrl,
    posterUrl: asset.posterUrl,
    loop: spec?.loop === true,
    width: asset.width,
    height: asset.height,
    durationSec: asset.durationSec,
    sizeBytes: asset.sizeBytes ?? null,
    fileType: (spec?.container ?? (isVideo ? 'mp4' : 'jpg')).toUpperCase(),
    version: asset.version,
    isLatest: asset.supersededAt === null,
    madeAt: asset.renderedAt ?? asset.createdAt,
    releaseThemeId: asset.releaseThemeId,
    watermark: fileHasWatermark(asset),
    preview: asset.preview === true,
    asset,
    reel: null,
  };
}

/**
 * The viewer item of a ready or approved reel with a video, else null. An AI reel is 9:16: the
 * server gives its size (540 x 960 for a preview); an older answer reads 1080 x 1920.
 */
export function reelViewerItem(reel: SerializedReel): AssetViewerItem | null {
  if ((reel.status !== 'ready' && reel.status !== 'approved') || reel.video === null) {
    return null;
  }
  const frame = { width: reel.width ?? 1080, height: reel.height ?? 1920 };
  return {
    key: reelViewerKey(reel.uuid),
    kind: 'reel',
    assetUuid: reel.uuid,
    themeId: 'aiReel',
    variant: null,
    specId: null,
    mediaKind: 'video',
    fileUrl: reel.video.url,
    playUrl: reel.video.url,
    posterUrl: reel.video.posterUrl,
    loop: false,
    width: frame.width,
    height: frame.height,
    durationSec: reel.durationSec,
    sizeBytes: reel.videoSizeBytes ?? null,
    fileType: 'MP4',
    version: null,
    isLatest: true,
    madeAt: reel.createdAt,
    releaseThemeId: reel.themeId,
    watermark: fileHasWatermark(reel),
    preview: reel.preview === true,
    asset: null,
    reel,
  };
}

/** The place of `key` in the list; 0 when the list does not hold it. */
export function viewerIndexOf(items: AssetViewerItem[], key: string): number {
  const index = items.findIndex((item) => item.key === key);
  return index < 0 ? 0 : index;
}
